import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field } from '../../../../components/Field';
import { TextArea } from '../../../../components/TextArea';
import { Icon } from '../../../../components/Icon/Icon';
import type { BlogFaqItem, BlogSource } from '../../../../mock-data/types';
import { useRowKeys } from '../../hooks/useRowKeys';
import { useSortableRows } from '../../hooks/useSortableRows';
import { GeneratingWash } from '../GeneratingWash';
import {
  Group,
  GroupLabel,
  Section,
  SectionHead,
  SectionTitleText,
  SectionCount,
  SectionBody,
  SectionEmpty,
  GhostRow,
  GhostBar,
  AddRow,
  AddButton,
  List,
  Row,
  Handle,
  Position,
  RowFields,
  PairRow,
  RemoveButton,
  TagBox,
  TagChip,
  TagRemove,
  TagInputEl,
  TagHint,
} from './SeoLists.styles';

/**
 * The small list builders of the editor's SEO section: string lists (key
 * takeaways), FAQ pairs, sources and free-form tag inputs. All controlled; the
 * page owns the arrays.
 *
 * The three list builders are one control with three row shapes — a bounded
 * section with its own head, a count, drag-sortable rows and a secondary add
 * button. Order is not decoration in any of them: key takeaways print in order
 * at the top of the article, the FAQ becomes an ordered `FAQPage` block in the
 * page's structured data, and sources print as a numbered list. Before this the
 * only way to move an entry was to retype two of them.
 */

const updateAt = <T,>(items: T[], index: number, value: T): T[] => {
  const next = items.slice();
  next[index] = value;
  return next;
};

const removeAt = <T,>(items: T[], index: number): T[] => items.filter((_, i) => i !== index);

const moveTo = <T,>(items: T[], from: number, to: number): T[] => {
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/* ── The shared section shell ── */

type ListSectionProps = {
  title: string;
  addLabel: string;
  /** Shown in place of the list when there is nothing in it yet. */
  emptyText: string;
  /** Names row `index` for its handle, e.g. "Key takeaway 2". */
  rowName: (index: number) => string;
  keys: string[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onMove: (from: number, to: number) => void;
  /** Something is being written somewhere on the page: the controls go inert. */
  busy?: boolean;
  /** THIS list is being written: it wears the generating wash. */
  generating?: boolean;
  /** Renders the fields of row `index`. */
  children: (index: number) => React.ReactNode;
};

/**
 * One bounded, sortable list. Everything except the fields inside a row is the
 * same across the three, which is the point: an author learns the control once.
 */
const ListSection = ({
  title,
  addLabel,
  emptyText,
  rowName,
  keys,
  onAdd,
  onRemove,
  onMove,
  busy = false,
  generating = false,
  children,
}: ListSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const { listRef, row, handleProps } = useSortableRows({ keys, onMove });

  return (
    <Section>
      <SectionHead>
        <SectionTitleText text={title} tag="span" />
        <SectionCount text={String(keys.length)} tag="span" />
      </SectionHead>
      <SectionBody $host={generating}>
        {keys.length === 0 ? (
          generating ? (
            /*
             * A list with nothing in it has nothing to wash, and an empty box
             * under a wash reads as a bug rather than as work in progress. One
             * placeholder row stands in for the entries about to arrive.
             */
            <List>
              <GhostRow aria-hidden="true">
                <GhostBar $wide />
                <GhostBar />
              </GhostRow>
            </List>
          ) : (
            <SectionEmpty text={emptyText} tag="span" />
          )
        ) : (
          <List ref={listRef}>
            {keys.map((key, index) => {
              const state = row(index);
              return (
                <Row key={key} $dragged={state.dragged} $shift={state.shift} $animated={state.animated}>
                  <Handle
                    type="button"
                    aria-label={t('Reorder {{name}}', { name: rowName(index) })}
                    disabled={busy}
                    {...handleProps(index)}
                  >
                    <Icon name="drag-handle" size={18} />
                  </Handle>
                  <Position text={String(state.position + 1)} tag="span" />
                  {children(index)}
                  <RemoveButton
                    type="button"
                    aria-label={t('Remove {{name}}', { name: rowName(index) })}
                    disabled={busy}
                    onClick={() => onRemove(index)}
                  >
                    <Icon name="minus" size={16} />
                  </RemoveButton>
                </Row>
              );
            })}
          </List>
        )}
        <AddRow>
          <AddButton label={addLabel} variant="outline" icon="plus" disabled={busy} onClick={onAdd} />
        </AddRow>
        {generating ? <GeneratingWash label={t('Lumio is writing this section')} /> : null}
      </SectionBody>
    </Section>
  );
};

/* ── String list (key takeaways) ── */

export type StringListEditorProps = {
  label: string;
  /** The per-row accessible label ("Key takeaway"). */
  rowLabel: string;
  addLabel: string;
  emptyText: string;
  values: string[];
  onChange: (values: string[]) => void;
  busy?: boolean;
  generating?: boolean;
};

export const StringListEditor = ({
  label,
  rowLabel,
  addLabel,
  emptyText,
  values,
  onChange,
  busy = false,
  generating = false,
}: StringListEditorProps): React.ReactElement => {
  const { t } = useTranslation();
  const rows = useRowKeys(values.length);

  return (
    <ListSection
      title={label}
      addLabel={addLabel}
      emptyText={emptyText}
      rowName={(index) => `${t(rowLabel)} ${index + 1}`}
      keys={rows.keys}
      busy={busy}
      generating={generating}
      onAdd={() => onChange([...values, ''])}
      onRemove={(index) => {
        rows.remove(index);
        onChange(removeAt(values, index));
      }}
      onMove={(from, to) => {
        rows.move(from, to);
        onChange(moveTo(values, from, to));
      }}
    >
      {(index) => (
        <RowFields>
          <Field
            label={rowLabel}
            value={values[index]}
            disabled={busy}
            onChange={(next) => onChange(updateAt(values, index, next))}
          />
        </RowFields>
      )}
    </ListSection>
  );
};

/* ── FAQ pairs ── */

export type FaqEditorProps = {
  values: BlogFaqItem[];
  onChange: (values: BlogFaqItem[]) => void;
  busy?: boolean;
  generating?: boolean;
};

export const FaqEditor = ({ values, onChange, busy = false, generating = false }: FaqEditorProps): React.ReactElement => {
  const { t } = useTranslation();
  const rows = useRowKeys(values.length);

  return (
    <ListSection
      title={t('FAQ')}
      addLabel={t('Add a question')}
      emptyText={t('Questions and answers, printed under the article and published as structured data.')}
      rowName={(index) => `${t('Question')} ${index + 1}`}
      keys={rows.keys}
      busy={busy}
      generating={generating}
      onAdd={() => onChange([...values, { q: '', a: '' }])}
      onRemove={(index) => {
        rows.remove(index);
        onChange(removeAt(values, index));
      }}
      onMove={(from, to) => {
        rows.move(from, to);
        onChange(moveTo(values, from, to));
      }}
    >
      {(index) => (
        <RowFields>
          <Field
            label={t('Question')}
            value={values[index].q}
            disabled={busy}
            onChange={(q) => onChange(updateAt(values, index, { ...values[index], q }))}
          />
          <TextArea
            label={t('Answer')}
            rows={2}
            value={values[index].a}
            disabled={busy}
            onChange={(a) => onChange(updateAt(values, index, { ...values[index], a }))}
          />
        </RowFields>
      )}
    </ListSection>
  );
};

/* ── Sources ── */

export type SourcesEditorProps = {
  values: BlogSource[];
  onChange: (values: BlogSource[]) => void;
  busy?: boolean;
  generating?: boolean;
};

export const SourcesEditor = ({
  values,
  onChange,
  busy = false,
  generating = false,
}: SourcesEditorProps): React.ReactElement => {
  const { t } = useTranslation();
  const rows = useRowKeys(values.length);

  return (
    <ListSection
      title={t('Sources')}
      addLabel={t('Add a source')}
      emptyText={t("What the article's claims rest on, printed as a numbered list at the end.")}
      rowName={(index) => `${t('Source')} ${index + 1}`}
      keys={rows.keys}
      busy={busy}
      generating={generating}
      onAdd={() => onChange([...values, { title: '', url: '' }])}
      onRemove={(index) => {
        rows.remove(index);
        onChange(removeAt(values, index));
      }}
      onMove={(from, to) => {
        rows.move(from, to);
        onChange(moveTo(values, from, to));
      }}
    >
      {(index) => (
        <PairRow>
          <RowFields>
            <Field
              label={t('Source title')}
              value={values[index].title}
              disabled={busy}
              onChange={(title) => onChange(updateAt(values, index, { ...values[index], title }))}
            />
          </RowFields>
          <RowFields>
            <Field
              label={t('URL')}
              value={values[index].url}
              disabled={busy}
              onChange={(url) => onChange(updateAt(values, index, { ...values[index], url }))}
            />
          </RowFields>
        </PairRow>
      )}
    </ListSection>
  );
};

/* ── Tags / entities ── */

export type TagInputProps = {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  /**
   * Space finishes the entry too, not just Enter and comma. Typing a word and
   * reaching for the space bar is how these lists are actually filled in.
   *
   * The cost is that a multi-word value cannot be typed straight through: it is
   * entered as separate entries, or pasted. That is the accepted trade for both
   * lists here, tags and entities alike.
   */
  commitOnSpace?: boolean;
  busy?: boolean;
  generating?: boolean;
};

export const TagInput = ({
  label,
  values,
  onChange,
  placeholder,
  commitOnSpace = false,
  busy = false,
  generating = false,
}: TagInputProps): React.ReactElement => {
  const { t } = useTranslation();
  const [pending, setPending] = React.useState('');

  const commit = (): void => {
    const value = pending.trim().replace(/,+$/, '');
    setPending('');
    if (value && !values.includes(value)) onChange([...values, value]);
  };

  return (
    <Group>
      <GroupLabel text={label} tag="span" />
      <TagBox $host={generating}>
        {values.map((value) => (
          <TagChip key={value}>
            {value}
            <TagRemove
              type="button"
              aria-label={t('Remove {{name}}', { name: value })}
              disabled={busy}
              onClick={() => onChange(values.filter((v) => v !== value))}
            >
              <Icon name="close" size={10} />
            </TagRemove>
          </TagChip>
        ))}
        <TagInputEl
          value={pending}
          aria-label={t(label)}
          placeholder={placeholder ? t(placeholder) : undefined}
          disabled={busy}
          onChange={(event) => setPending(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',' || (commitOnSpace && event.key === ' ')) {
              // A space with nothing typed yet is a stray keystroke, not an empty
              // entry: let it through rather than swallowing it.
              if (event.key === ' ' && pending.trim() === '') return;
              event.preventDefault();
              commit();
            }
            if (event.key === 'Backspace' && pending === '' && values.length > 0) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={commit}
        />
        {generating ? <GeneratingWash label={t('Lumio is writing this section')} /> : null}
      </TagBox>
      <TagHint
        text={commitOnSpace ? t('Space, Enter or a comma finishes an entry.') : t('Enter or a comma finishes an entry.')}
        tag="span"
      />
    </Group>
  );
};
